'use client'

import { Button, Card, CardContent, getHrefWithThreadPreservation } from '@yeshuman/sdk'
import { Link, useParams } from 'react-router-dom'

import type { JobProspectEmptyReason } from '../../hooks/use-job-prospect-matches'

type JobWorkspaceSuggestionsEmptyProps = {
  emptyReason?: JobProspectEmptyReason | null
}

function emptyCopy(emptyReason?: JobProspectEmptyReason | null): {
  title: string
  body: string
  showTalentLink: boolean
} {
  switch (emptyReason) {
    case 'opportunity_incomplete':
      return {
        title: 'Finish setting up this job',
        body: 'Add a job description and at least one skill to get suggestions.',
        showTalentLink: false,
      }
    case 'embedding_unavailable':
      return {
        title: 'Suggestions unavailable right now',
        body: 'We could not prepare this job for matching. Try again later, or review Talent for people already in your pipeline.',
        showTalentLink: true,
      }
    case 'no_embedded_profiles':
      return {
        title: 'No suggestions yet',
        body: 'There are not enough profiles in the network for a quick shortlist. Check back later, or review Talent for applicants and invites.',
        showTalentLink: true,
      }
    case 'all_matched_applied':
      return {
        title: 'No new suggestions',
        body: 'People who match this role closely have already applied. Review Pipeline or Talent.',
        showTalentLink: true,
      }
    case 'no_strong_matches':
    default:
      return {
        title: 'No suggestions yet',
        body: 'We did not find people outside your applicant pool who match strongly enough. Check Talent for ranked applicants, or check back later.',
        showTalentLink: true,
      }
  }
}

export function JobWorkspaceSuggestionsEmpty({ emptyReason }: JobWorkspaceSuggestionsEmptyProps) {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const { title, body, showTalentLink } = emptyCopy(emptyReason)
  const talentHref = getHrefWithThreadPreservation(`/employer/jobs/${jobId}/talent`)

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center text-sm">
        <p className="font-medium text-foreground">{title}</p>
        <p className="max-w-md text-muted-foreground">{body}</p>
        {showTalentLink && jobId ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={talentHref}>Open Talent</Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}
